/**
 * BootRealIOS — Layer 2 of the Reconnecting-WebSocket architecture.
 *
 * Workflow (per user spec):
 *   1. Pro user clicks "Boot Real IOS" button (otherwise → UpgradeModal)
 *   2. Component POSTs /api/cml/sessions/{lab_id} (v1) → CML lab provisions
 *   3. Polls GET /cml/sessions/{id} every 3s until status=ready (~3 min)
 *   4. POSTs /api/cml/v2/sessions {lab_id, device_id} → SessionManager attaches
 *   5. Opens WS /api/cml/v2/console/{id}?token=...&resume_from=cursorHead
 *      - On close: reconnect in 300ms with the latest cursorHead
 *      - 25s keepalive ping (Layer 3 inline)
 *   6. Waits for {"type":"ios_ready"} JSON event from server
 *   7. POSTs /cml/v2/sessions/{id}/replay {commands: sim history}
 *      - Disables input during replay
 *      - On any invalid command: shows which one failed
 *   8. Replay complete → enables input → student types live IOS commands
 *
 * End Session button cleans up both v2 and v1.
 *
 * Rendering: xterm.js for byte-accurate console (real CML emits escapes,
 * partial lines, etc. — a line-based renderer would mangle the output).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { toast } from "sonner";
import { api } from "../lib/api";
import useLabSounds, { outputHasIosError } from "../hooks/useLabSounds";
import { BootSequence } from "./BootSequence";

// Reconnect tuning
// Silent-retry design: student sees ONLY "Booting Cisco IOS Router…" the whole
// way through boot + all reconnect attempts. No amber banner, no "reconnecting"
// text, no attempt counters.
// Kept in sync with backend session_manager.UPSTREAM_RECONNECT_BACKOFF.
const RECONNECT_BACKOFFS_MS = [500, 1000, 2000, 4000, 6000, 8000];
const MAX_CONSEC_FAILS = RECONNECT_BACKOFFS_MS.length;   // 6 WS attempts
const KEEPALIVE_INTERVAL_MS = 25000;
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 6 * 60 * 1000;       // 6 min max boot
const EST_BOOT_SEC = 25;
// Default idle policy; the v1 create response carries the live value
// (backend CML_IDLE_TIMEOUT_SEC). Warning band starts 2 min before the end.
const DEFAULT_IDLE_END_SEC = 600;
const IDLE_WARN_BEFORE_SEC = 120;

// Lifecycle phases — drives the button + status banner
const PHASE = {
  IDLE: "idle",
  CHECK_SLOTS: "check_slots",
  BOOTING: "booting",                 // v1 provisioned, polling
  CONNECTING: "connecting",           // v2 created, WS opening
  WAITING_PROMPT: "waiting_prompt",   // WS open, no ios_ready yet
  REPLAYING: "replaying",
  READY: "ready",                     // student can type
  ENDING: "ending",
  ERROR: "error",
};

// Retry a request 3× with 1.2 s backoff. Only retries fast 5xx/429/network
// errors; slow 5xx (>5 s) likely completed upstream and would leak on retry.
async function withRetry(label, fn) {
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAY_MS = 1200;
  const SLOW_RESPONSE_THRESHOLD_MS = 5000;
  let lastErr = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const t0 = Date.now();
    try {
      if (attempt > 1) await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
      const result = await fn();
      if (attempt > 1) console.info(`[BootRealIOS] ${label} succeeded on attempt ${attempt}/${MAX_ATTEMPTS}`);
      return { ok: true, value: result };
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      const elapsed = Date.now() - t0;
      const isTransient = (!status || status >= 500 || status === 429) && elapsed < SLOW_RESPONSE_THRESHOLD_MS;
      console.warn(`[BootRealIOS] ${label} attempt ${attempt}/${MAX_ATTEMPTS} failed`,
        { status, elapsed_ms: elapsed, transient: isTransient, detail: e?.response?.data?.detail });
      if (!isTransient) break;
    }
  }
  return { ok: false, error: lastErr };
}

export default function BootRealIOS({
  labId,
  deviceId = "R1",
  devices = [],           // [{id, device_type}] — rows in the boot monitor
  simSessionId,           // current Python-sim session_id (for /history)
  isPro,
  onModeChange,           // callback("simulator" | "real_ios")
  onExit,                 // callback() invoked after End Session cleanup
  onV2SessionChange,      // callback(v2SessionId | null) — for Grade gating in parent
}) {
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [statusMsg, setStatusMsg] = useState("");
  const [bootElapsed, setBootElapsed] = useState(0);
  const [slots, setSlots] = useState(null);              // {live_total, ...}
  const [v1SessionId, setV1SessionId] = useState(null);
  const [v2SessionId, setV2SessionId] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [reconnects, setReconnects] = useState(0);
  const [replayProgress, setReplayProgress] = useState(null);  // {idx, total, current}
  const [switching, setSwitching] = useState(false);   // multi-device console switch in flight
  const [idleLeft, setIdleLeft] = useState(null);      // seconds until idle auto-end (warning band)
  const [notice, setNotice] = useState(null);          // shown in IDLE state after an auto-end
  const lastKeyRef = useRef(Date.now());
  const idleEndSecRef = useRef(DEFAULT_IDLE_END_SEC);

  // Refs that need to survive renders without triggering reflow
  const termContainerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const wsRef = useRef(null);
  const cursorRef = useRef(0);
  const tokenRef = useRef(null);
  const keepaliveRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const consecFailsRef = useRef(0);
  const onMsgReadyRef = useRef(null);    // resolves once ios_ready arrives
  const pollAbortRef = useRef(false);
  const v1SessionIdRef = useRef(null);   // mirror so refs can trigger v2 re-attach
  const v2SessionIdRef = useRef(null);
  const labIdRef = useRef(labId);
  const deviceIdRef = useRef(deviceId);
  const attemptV2ReattachRef = useRef(null);
  const reattachedOnceRef = useRef(false);   // one auto v2 re-attach per boot
  const v1ReadyRef = useRef(false);          // v1 lab confirmed ready → pool slot alive
  const attachV2AndOpenWsRef = useRef(null); // exposed for handleRetry fast-path
  const attachedDeviceRef = useRef(null);    // device whose console the WS is on
  const replayedDevicesRef = useRef(new Set()); // devices already seeded from sim history
  const switchingRef = useRef(false);
  // Round 39 — audio feedback. Rolling 512-char buffer of incoming CML bytes
  // (decoded, ANSI-stripped) is inspected 700 ms after each Enter keystroke
  // so we can play cmd-right / cmd-wrong.
  const sounds = useLabSounds();
  const recentOutputRef = useRef("");
  const cmdSoundTimerRef = useRef(null);
  const textDecoderRef = useRef(new TextDecoder("utf-8", { fatal: false }));

  // ----- xterm boot ---------------------------------------------------------
  useEffect(() => {
    if (!termContainerRef.current || termRef.current) return;
    const term = new XTerm({
      fontFamily: '"Fira Code", "Cascadia Code", "JetBrains Mono", Menlo, Consolas, monospace',
      fontSize: 13,
      lineHeight: 1.2,
      // Match the simulator Terminal palette: pure black, neon green accent.
      theme: {
        background: "#000000",
        foreground: "#e4e4e7",       // zinc-100 — same as sim
        cursor: "#00FF41",            // sim's --c-prompt (green)
        cursorAccent: "#000000",
        selectionBackground: "#00FF4133",
        green: "#00FF41",
        brightGreen: "#00FF41",
      },
      cursorBlink: true,
      scrollback: 4000,
      convertEol: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(termContainerRef.current);
    try { fit.fit(); } catch { /* container may be hidden */ }
    termRef.current = term;
    fitRef.current = fit;

    // Stream typed bytes upstream when phase=READY. xterm captures keystrokes
    // and forwards each one as a text frame to CML — same as a real serial
    // console (every char sent on press, IOS echoes it back).
    term.onData((data) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (phaseRef.current !== PHASE.READY) return;
      try { ws.send(data); } catch { /* reconnect path handles */ }
      lastKeyRef.current = Date.now();
      setIdleLeft(null);
      // Round 39 — schedule an IOS-error check ~700 ms after each Enter
      // press so we can play cmd-right / cmd-wrong feedback. 700 ms is
      // long enough for the router to echo + emit "% Invalid…" before
      // we sample the buffer.
      if (data === "\r" || data === "\n" || data === "\r\n") {
        if (cmdSoundTimerRef.current) clearTimeout(cmdSoundTimerRef.current);
        recentOutputRef.current = "";   // reset window to only capture new response
        cmdSoundTimerRef.current = setTimeout(() => {
          const buf = recentOutputRef.current || "";
          // Ignore trivial echoes (bare prompt reprints — e.g. after blank Enter).
          if (buf.replace(/[\s\r\n]/g, "").length < 2) return;
          if (outputHasIosError(buf)) sounds.playCmdWrong();
          else sounds.playCmdRight();
        }, 700);
      }
    });

    const ro = new ResizeObserver(() => { try { fit.fit(); } catch { /* */ } });
    ro.observe(termContainerRef.current);
    return () => {
      ro.disconnect();
      term.dispose();
      termRef.current = null;
    };
  }, []);

  // Mirror phase into a ref so the xterm onData closure sees the latest value
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Keep id refs in sync so the WS reconnect closures always have the latest.
  useEffect(() => { v1SessionIdRef.current = v1SessionId; }, [v1SessionId]);
  useEffect(() => { v2SessionIdRef.current = v2SessionId; }, [v2SessionId]);
  useEffect(() => { labIdRef.current = labId; }, [labId]);
  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);

  // Notify parent on mode change
  useEffect(() => {
    if (!onModeChange) return;
    const mode = (
      phase === PHASE.READY || phase === PHASE.REPLAYING || phase === PHASE.WAITING_PROMPT
    ) ? "real_ios" : "simulator";
    onModeChange(mode);
  }, [phase, onModeChange]);

  // ----- helpers ------------------------------------------------------------
  const writeLine = (txt, color = "") => {
    const t = termRef.current;
    if (!t) return;
    t.writeln(color ? `\x1b[${color}m${txt}\x1b[0m` : txt);
  };

  const closeWs = useCallback(() => {
    if (keepaliveRef.current) { clearInterval(keepaliveRef.current); keepaliveRef.current = null; }
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && ws.readyState <= WebSocket.OPEN) {
      try { ws.close(1000, "client teardown"); } catch { /* */ }
    }
  }, []);

  // ----- server-side session end (idle timeout / replaced / lab gone) ------
  const handleSessionEndedRef = useRef(null);
  const failOrEndedRef = useRef(null);
  const handleSessionEnded = useCallback((reason) => {
    if (phaseRef.current === PHASE.IDLE || phaseRef.current === PHASE.ENDING) return;
    pollAbortRef.current = true;
    closeWs();
    v1SessionIdRef.current = null;
    v2SessionIdRef.current = null;
    v1ReadyRef.current = false;
    attachedDeviceRef.current = null;
    setV1SessionId(null);
    setV2SessionId(null);
    if (onV2SessionChange) onV2SessionChange(null);
    cursorRef.current = 0;
    setIdleLeft(null);
    setReplayProgress(null);
    setErrorMsg(null);
    const msg =
      reason === "idle_timeout" ? `Session ended after ${Math.round(idleEndSecRef.current / 60)} minutes of inactivity — the node was returned to the pool for the next student.` :
      reason === "replaced_by_new_session" ? "This session was replaced by a newer Real IOS session in another tab." :
      "Your Real IOS session has ended.";
    setNotice(msg);
    setPhase(PHASE.IDLE);
    setStatusMsg("");
    if (termRef.current) termRef.current.write(`\r\n\x1b[33m[${msg}]\x1b[0m\r\n`);
    toast.info(msg);
  }, [closeWs, onV2SessionChange]);
  useEffect(() => { handleSessionEndedRef.current = handleSessionEnded; }, [handleSessionEnded]);

  // On a hard WS failure, ask the backend whether v1 simply ended (idle /
  // replaced) before showing the generic connection error.
  useEffect(() => {
    failOrEndedRef.current = async () => {
      const v1 = v1SessionIdRef.current;
      if (v1) {
        try {
          const r = await api.get(`/cml/sessions/${v1}`);
          if (r.data.status === "ended") { handleSessionEnded(r.data.ended_reason); return; }
        } catch { /* fall through */ }
      }
      setPhase(PHASE.ERROR);
      setErrorMsg("Unable to connect to Cisco IOS Router. Please try again.");
    };
  }, [handleSessionEnded]);

  // Idle countdown band: warn from IDLE_WARN_SEC of no keystrokes.
  useEffect(() => {
    if (phase !== PHASE.READY) { setIdleLeft(null); return undefined; }
    lastKeyRef.current = Date.now();
    const id = setInterval(() => {
      const idle = (Date.now() - lastKeyRef.current) / 1000;
      const end = idleEndSecRef.current;
      setIdleLeft(idle >= end - IDLE_WARN_BEFORE_SEC ? Math.max(0, Math.round(end - idle)) : null);
    }, 5000);
    return () => clearInterval(id);
  }, [phase]);

  // ----- WebSocket open + reconnect loop ------------------------------------
  const openWs = useCallback(async (v2Id) => {
    // Fetch a short-lived JWT for the WS query-param auth
    if (!tokenRef.current) {
      try {
        const r = await api.get("/auth/ws-token");  // see below — we'll wire this
        tokenRef.current = r.data.token;
      } catch (e) {
        // Fallback: try the cookie-based session — backend WS handler also
        // accepts ?access_token if Authorization isn't usable
        console.warn("ws-token endpoint unavailable, falling back to access_token", e);
      }
    }

    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    const wsScheme = apiBase.startsWith("https") ? "wss" : "ws";
    const host = apiBase.replace(/^https?:\/\//, "");
    const token = tokenRef.current || "";
    const url = `${wsScheme}://${host}/api/cml/v2/console/${v2Id}?token=${encodeURIComponent(token)}&resume_from=${cursorRef.current}`;

    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      consecFailsRef.current = 0;
      // 25s keepalive (Layer 3 inline) — text JSON frame so backend treats
      // it as a control message and drops it silently
      if (keepaliveRef.current) clearInterval(keepaliveRef.current);
      keepaliveRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send("__ping__"); } catch { /* */ }
        }
      }, KEEPALIVE_INTERVAL_MS);
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") {
        // Control event (JSON text frame)
        try {
          const evt = JSON.parse(ev.data);
          if (evt.type === "ios_ready" && onMsgReadyRef.current) {
            onMsgReadyRef.current(evt);
            onMsgReadyRef.current = null;
          } else if (evt.type === "cml_state") {
            // Silent-retry policy: do NOT surface "reconnecting" / "connected"
            // to the student. Terminal stays clean; header keeps showing
            // "booting Cisco IOS…" throughout. Only permanent-death
            // is surfaced (below).
            if (evt.state === "dead") {
              failOrEndedRef.current?.();
            }
            setReconnects(evt.reconnects || 0);
          } else if (evt.type === "replay_progress") {
            setReplayProgress({ idx: evt.index + 1, current: evt.command, status: evt.status });
          } else if (evt.type === "replay_finished") {
            // handled by promise in startReplay()
          } else if (evt.type === "session_ended") {
            handleSessionEndedRef.current?.(evt.reason);
          } else if (evt.type === "fatal") {
            // Silent: no raw error text to the student. Just show error state.
            console.warn("[BootRealIOS] fatal event:", evt.reason);
            failOrEndedRef.current?.();
          }
        } catch (e) {
          // ignore malformed control frames
        }
        return;
      }
      // Binary frame → write to terminal + bump cursor
      const bytes = new Uint8Array(ev.data);
      cursorRef.current += bytes.length;
      const t = termRef.current;
      if (t) t.write(bytes);
      // Round 39 — decode & append to a small rolling buffer so the post-
      // Enter timer can classify the response. ANSI escape sequences are
      // stripped to make error-pattern matching reliable.
      try {
        const chunk = textDecoderRef.current.decode(bytes, { stream: true });
        // Strip common ANSI: CSI sequences (\x1b[...letters) and lone CRs.
        const clean = chunk.replace(/\x1b\[[0-9;?]*[A-Za-z]/g, "");
        const combined = (recentOutputRef.current + clean).slice(-512);
        recentOutputRef.current = combined;
      } catch { /* decoder never throws with fatal:false */ }
    };

    ws.onerror = () => {
      // Counter bump happens in onclose (always fires) to avoid double-count.
    };

    ws.onclose = (ev) => {
      if (keepaliveRef.current) { clearInterval(keepaliveRef.current); keepaliveRef.current = null; }
      // Stale socket (torn down by closeWs for a switch/retry/end) — never
      // reconnect or surface errors for a console we deliberately left.
      if (wsRef.current !== ws) return;
      // Don't reconnect on app-level fatal codes
      if ([4001, 4002, 4003, 4004, 1000].includes(ev.code)) {
        if (ev.code === 4001) {
          // superseded — silent; another tab took over
          return;
        }
        // Silent: no raw close-code text to the student.
        console.warn(`[BootRealIOS] ws closed code=${ev.code} reason="${ev.reason || "n/a"}"`);
        if (ev.code !== 1000) {
          failOrEndedRef.current?.();
        }
        return;
      }
      // Silent reconnect with exponential backoff. Header keeps showing
      // "booting Cisco IOS…" (during WAITING_PROMPT phase) or the
      // terminal stays visible unchanged (during READY phase) — no user-
      // facing artifacts of the retry attempts.
      const failIdx = consecFailsRef.current;
      if (failIdx >= MAX_CONSEC_FAILS) {
        // WS-level retries exhausted. Final fallback: re-attach v2 (fresh
        // session id against the same still-running v1 lab) and try the
        // WS handshake ONE more time with a fresh 6-attempt budget.
        console.warn(`[BootRealIOS] WS reconnects exhausted (${MAX_CONSEC_FAILS}) — attempting v2 re-attach`);
        reconnectTimerRef.current = setTimeout(() => {
          attemptV2ReattachRef.current?.();
        }, 800);
        return;
      }
      const delay = RECONNECT_BACKOFFS_MS[failIdx] || RECONNECT_BACKOFFS_MS[RECONNECT_BACKOFFS_MS.length - 1];
      consecFailsRef.current = failIdx + 1;
      console.debug(`[BootRealIOS] WS reconnect attempt ${failIdx + 1}/${MAX_CONSEC_FAILS} in ${delay}ms`);
      reconnectTimerRef.current = setTimeout(() => {
        openWs(v2Id);
      }, delay);
    };

    return ws;
  }, []);

  // ----- Silent v2 re-attach fallback --------------------------------------
  // Fired when the WS-level 6-retry budget is exhausted. Creates a fresh v2
  // session (against the still-running v1 lab) and re-opens the WS. Runs at
  // most ONCE per boot; if it also fails, we surface the error banner.
  const attemptV2Reattach = useCallback(async () => {
    if (reattachedOnceRef.current) {
      console.warn("[BootRealIOS] v2 re-attach already tried once — giving up");
      setPhase(PHASE.ERROR);
      setErrorMsg("Unable to connect to Cisco IOS Router. Please try again.");
      return;
    }
    reattachedOnceRef.current = true;
    const v1Id = v1SessionIdRef.current;
    if (!v1Id) {
      setPhase(PHASE.ERROR);
      setErrorMsg("Unable to connect to Cisco IOS Router. Please try again.");
      return;
    }
    try {
      console.info("[BootRealIOS] attempting silent v2 re-attach");
      const r = await api.post(`/cml/v2/sessions`, {
        lab_id: labIdRef.current,
        device_id: deviceIdRef.current,
        v1_session_id: v1SessionIdRef.current,
      });
      const newV2Id = r.data.session_id;
      cursorRef.current = r.data.cursor_head || 0;
      v2SessionIdRef.current = newV2Id;
      setV2SessionId(newV2Id);
      if (onV2SessionChange) onV2SessionChange(newV2Id);
      // Reset WS retry counter and try again with fresh v2 id
      consecFailsRef.current = 0;
      await openWs(newV2Id);
    } catch (e) {
      console.error("[BootRealIOS] v2 re-attach failed:", e);
      setPhase(PHASE.ERROR);
      setErrorMsg("Unable to connect to Cisco IOS Router. Please try again.");
    }
  }, [openWs, onV2SessionChange]);

  // Wire the ref so openWs's onclose closure can invoke it
  useEffect(() => {
    attemptV2ReattachRef.current = attemptV2Reattach;
  }, [attemptV2Reattach]);

  // ----- replay -------------------------------------------------------------
  const startReplay = useCallback(async (v2Id, devId) => {
    if (!simSessionId || replayedDevicesRef.current.has(devId)) {
      setPhase(PHASE.READY);
      return;
    }
    replayedDevicesRef.current.add(devId);
    let history = [];
    try {
      const r = await api.get(`/labs/session/${simSessionId}/history`, { params: { device_id: devId } });
      history = r.data.history || [];
    } catch (e) {
      console.warn("fetch sim history failed", e);
    }
    if (!history.length) {
      setPhase(PHASE.READY);
      return;
    }
    setPhase(PHASE.REPLAYING);
    setReplayProgress({ idx: 0, total: history.length });
    writeLine(`\r\n[replaying ${history.length} commands from your sim session…]`, "36");
    try {
      const r = await api.post(`/cml/v2/sessions/${v2Id}/replay`, {
        commands: history,
        continue_on_error: false,
      });
      const { results, aborted_at } = r.data;
      if (aborted_at !== null && aborted_at !== undefined) {
        const failed = results[aborted_at];
        writeLine(`\r\n[replay stopped at command #${aborted_at + 1}: "${failed.command}"]`, "31");
        writeLine(`   ${failed.invalid_input ? "% Invalid input" : failed.incomplete ? "% Incomplete" : failed.ambiguous ? "% Ambiguous" : "(no prompt detected — timeout)"}`, "31");
        toast.error(`Real IOS rejected command #${aborted_at + 1}: ${failed.command}`);
      } else {
        writeLine(`\r\n[replay complete — ${results.length} commands applied to real hardware]`, "32");
        toast.success("All commands replayed to real IOS.");
      }
    } catch (e) {
      console.error("replay failed", e);
      writeLine(`\r\n[replay failed: ${e?.response?.data?.detail || e.message}]`, "31");
      toast.error("Replay failed — see console output.");
    }
    setReplayProgress(null);
    setPhase(PHASE.READY);
  }, [simSessionId]);

  // ----- Step 3-5 as a composable helper ------------------------------------
  //
  // Called by handleBoot (first-time) AND by handleRetry when v1 is already
  // ready. Reusing this on retry means the pool slot stays claimed — no new
  // CML lab is spawned. `withRetry` is the same helper handleBoot uses, but
  // we pass it in so we don't duplicate its config across call sites.
  const attachV2AndOpenWs = useCallback(async (v1Id, withRetryFn) => {
    // Step 3 — v2 create (with 3-attempt HTTP retry)
    setPhase(PHASE.CONNECTING);
    let v2Id;
    {
      const res = await withRetryFn("v2 attach", () =>
        api.post(`/cml/v2/sessions`, {
          lab_id: labIdRef.current, device_id: deviceIdRef.current,
          v1_session_id: v1SessionIdRef.current,
        })
      );
      if (!res.ok) {
        console.error("[BootRealIOS] v2 attach exhausted retries:", res.error);
        // POOL-SLOT-PRESERVATION (Round 37 Fix 2):
        // Do NOT end v1 here. v1 is healthy (poll returned ready), so the
        // pool slot is still viable. The student's "Retry Connection" click
        // will re-run just this v2 step against the SAME v1 → same pool slot.
        // Releasing v1 here would cause the pool to hand out a fresh slot on
        // retry, spawning a new CML lab and leaking orphans.
        const code = res.error?.response?.data?.detail?.error_code;
        if (code === "cml_lab_missing" || code === "v1_session_not_found" || code === "no_active_cml_lab") {
          // Server retired the stale v1 — a Retry will provision a fresh slot.
          v1SessionIdRef.current = null;
          setV1SessionId(null);
          setErrorMsg("Your reserved router was recycled. Click Retry to get a fresh one.");
        } else {
          setErrorMsg("Unable to connect to Cisco IOS Router. Please try again.");
        }
        setPhase(PHASE.ERROR);
        return false;
      }
      v2Id = res.value.data.session_id;
      cursorRef.current = res.value.data.cursor_head || 0;
      attachedDeviceRef.current = deviceIdRef.current;
      // Round 37 diagnostic — log the pod that CREATED this v2 session.
      // Compare with the pod embedded in the WS close-reason on 4002 to
      // prove/disprove multi-pod split-brain.
      const createPod = res.value.data._pod_id || "unknown";
      console.info(`[BootRealIOS] v2 created: session=${v2Id} pod=${createPod}`);
      setV2SessionId(v2Id);
      v2SessionIdRef.current = v2Id;
      if (onV2SessionChange) onV2SessionChange(v2Id);
    }

    // Step 4 — open WS + wait for ios_ready
    setPhase(PHASE.WAITING_PROMPT);
    const iosReadyPromise = new Promise((resolve) => {
      onMsgReadyRef.current = resolve;
    });
    // Reset the reconnect + auto-reattach counters for this v2 session.
    consecFailsRef.current = 0;
    reattachedOnceRef.current = false;
    await openWs(v2Id);
    try {
      await Promise.race([
        iosReadyPromise,
        new Promise((_, rej) => setTimeout(() => rej(new Error("ios_ready timeout after 90s")), 90000)),
      ]);
    } catch (e) {
      console.warn("ios_ready wait error:", e);
      // Continue anyway — terminal will display whatever bytes arrive
    }

    // Step 5 — auto-replay (only this device's sim history, once per device)
    await startReplay(v2Id, deviceIdRef.current);
    return true;
  }, [openWs, onV2SessionChange, startReplay]);

  // Wire the ref so handleRetry can call it without re-render churn
  useEffect(() => {
    attachV2AndOpenWsRef.current = attachV2AndOpenWs;
  }, [attachV2AndOpenWs]);

  // ----- multi-device console switch ----------------------------------------
  // Parent changes `deviceId` when the student clicks another node on the
  // topology. Same v1 lab (no new CML nodes): drop the current v2 console,
  // attach a fresh v2 to the requested node, reopen the WS. Re-checked on
  // every phase change so a click made mid-boot is honoured once READY.
  useEffect(() => {
    const attached = attachedDeviceRef.current;
    if (!attached || attached === deviceId || switchingRef.current) return;
    if (phase !== PHASE.READY || !v1SessionIdRef.current) return;
    switchingRef.current = true;
    (async () => {
      setSwitching(true);
      closeWs();
      const oldV2 = v2SessionIdRef.current;
      v2SessionIdRef.current = null;
      setV2SessionId(null);
      if (onV2SessionChange) onV2SessionChange(null);
      if (oldV2) { try { await api.delete(`/cml/v2/sessions/${oldV2}`); } catch { /* */ } }
      cursorRef.current = 0;
      if (termRef.current) termRef.current.reset();
      writeLine(`[console → ${deviceId}]`, "36");
      console.info(`[BootRealIOS] switching console ${attached} → ${deviceId}`);
      try {
        await attachV2AndOpenWsRef.current(v1SessionIdRef.current, withRetry);
      } finally {
        switchingRef.current = false;
        setSwitching(false);
      }
    })();
  }, [deviceId, phase, closeWs, onV2SessionChange]);

  // ----- main boot orchestration --------------------------------------------
  //
  // Student-facing UX rule: from click to prompt, the student sees ONE
  // status message: "Booting Cisco IOS Router…". Elapsed seconds tick up
  // during long cold-boots so it doesn't feel frozen. All infrastructure
  // details (CML, v2 attach, retries, pool acquires) are hidden — they're
  // logged to console for developer debug but never surfaced.
  const handleBoot = useCallback(async () => {
    if (!isPro) {
      toast.error("Real IOS labs require a Pro subscription.");
      return;
    }
    setErrorMsg(null);
    setNotice(null);
    setPhase(PHASE.CHECK_SLOTS);
    // Reset the one-shot v2 re-attach budget for this boot attempt.
    reattachedOnceRef.current = false;
    consecFailsRef.current = 0;
    v1ReadyRef.current = false;
    attachedDeviceRef.current = null;
    replayedDevicesRef.current = new Set();
    // Single student-facing status for the whole boot flow.
    const BOOTING_MSG = "Booting Cisco IOS Router…";
    setStatusMsg(BOOTING_MSG);

    // Step 1 — v1 provision (with retry)
    let v1Id;
    {
      const res = await withRetry("v1 provision", () =>
        api.post(`/cml/sessions/${labId}`)
      );
      if (!res.ok) {
        console.error("[BootRealIOS] v1 provision exhausted retries:", res.error);
        setErrorMsg("Unable to connect to Cisco IOS Router. Please try again.");
        setPhase(PHASE.ERROR);
        return;
      }
      v1Id = res.value.data.session_id;
      if (res.value.data.idle_timeout_sec) idleEndSecRef.current = res.value.data.idle_timeout_sec;
      setV1SessionId(v1Id);
      v1SessionIdRef.current = v1Id;
    }

    // Step 2 — poll until ready
    setPhase(PHASE.BOOTING);
    const t0 = Date.now();
    pollAbortRef.current = false;
    const tick = setInterval(() => setBootElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    try {
      while (Date.now() - t0 < POLL_TIMEOUT_MS) {
        if (pollAbortRef.current) throw new Error("boot cancelled");
        const r = await api.get(`/cml/sessions/${v1Id}`);
        const s = r.data.status;
        console.debug("[BootRealIOS] v1 status:", s);
        if (s === "ready") break;
        if (s === "error" || s === "failed" || s === "dead") {
          throw new Error(`CML lab failed: ${r.data.message || s}`);
        }
        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
      }
    } catch (e) {
      clearInterval(tick);
      console.error("[BootRealIOS] poll failed:", e);
      setErrorMsg("Unable to connect to Cisco IOS Router. Please try again.");
      setPhase(PHASE.ERROR);
      // v1 lab is genuinely broken here (poll returned error/dead/timeout).
      // Safe to release the pool slot.
      try { await api.post(`/cml/sessions/${v1Id}/end`); } catch { /* */ }
      v1SessionIdRef.current = null;
      setV1SessionId(null);
      return;
    }
    clearInterval(tick);
    // v1 is confirmed ready → pool slot is claimed & healthy. Retry can
    // skip provision and jump straight to v2 attach.
    v1ReadyRef.current = true;

    // Steps 3-5 — v2 attach + WS + replay (extracted for retry reuse)
    await attachV2AndOpenWs(v1Id, withRetry);
  }, [labId, isPro, attachV2AndOpenWs]);

  // ----- single-click retry -------------------------------------------------
  //
  // Student-facing rule: they never click "End Session → Real IOS → Boot"
  // manually for a transient failure. One click on "Retry Connection" does
  // the right thing based on where the failure occurred.
  //
  // POOL-SLOT-PRESERVATION (Round 37 Fix 2): if v1 is still healthy (poll
  // confirmed 'ready' before the failure), skip Step 1-2 and jump straight
  // to v2 attach. Same v1 lab → same pool slot → no new CML lab spawned.
  // Only if v1 has actually died (or was never provisioned) do we tear it
  // down and re-run the full boot.
  const handleRetry = useCallback(async () => {
    setErrorMsg(null);
    setBootElapsed(0);
    setReconnects(0);
    // Silent teardown of any lingering WS + v2 (we're rebuilding them).
    pollAbortRef.current = true;
    closeWs();
    const v2 = v2SessionIdRef.current;
    if (v2) { try { await api.delete(`/cml/v2/sessions/${v2}`); } catch { /* */ } }
    setV2SessionId(null);
    v2SessionIdRef.current = null;
    if (onV2SessionChange) onV2SessionChange(null);
    cursorRef.current = 0;
    consecFailsRef.current = 0;
    reattachedOnceRef.current = false;

    // Fast path: v1 healthy → skip provision, retry v2 attach + WS on the
    // SAME pool slot. No new CML lab. No orphan.
    const v1 = v1SessionIdRef.current;
    if (v1 && v1ReadyRef.current) {
      // Verify v1 is still alive on the backend before we bet on it.
      let v1StillReady = false;
      try {
        const r = await api.get(`/cml/sessions/${v1}`);
        v1StillReady = r.data.status === "ready";
      } catch (e) {
        console.warn("[BootRealIOS] retry: v1 status check failed:", e);
      }
      if (v1StillReady) {
        console.info("[BootRealIOS] retry fast-path — reusing v1", v1);
        setPhase(PHASE.CHECK_SLOTS);
        setStatusMsg("Booting Cisco IOS Router…");
        await attachV2AndOpenWs(v1, withRetry);
        return;
      }
      // v1 is genuinely dead — fall through to full teardown + reboot.
      console.warn("[BootRealIOS] retry: v1 no longer ready, tearing down for full reboot");
    }

    // Slow path: v1 broken or never provisioned. Full teardown + reboot.
    if (v1) { try { await api.post(`/cml/sessions/${v1}/end`); } catch { /* */ } }
    setV1SessionId(null);
    v1SessionIdRef.current = null;
    v1ReadyRef.current = false;
    attachedDeviceRef.current = null;
    setReplayProgress(null);
    setPhase(PHASE.IDLE);
    setStatusMsg("");
    setTimeout(() => { handleBoot(); }, 50);
  }, [closeWs, onV2SessionChange, handleBoot, attachV2AndOpenWs]);

  // ----- end session --------------------------------------------------------
  const handleEnd = useCallback(async () => {
    setPhase(PHASE.ENDING);
    pollAbortRef.current = true;
    closeWs();
    if (v2SessionId) {
      try { await api.delete(`/cml/v2/sessions/${v2SessionId}`); } catch { /* */ }
    }
    if (v1SessionId) {
      try { await api.post(`/cml/sessions/${v1SessionId}/end`); } catch { /* */ }
    }
    setV1SessionId(null);
    setV2SessionId(null);
    if (onV2SessionChange) onV2SessionChange(null);
    cursorRef.current = 0;
    consecFailsRef.current = 0;
    attachedDeviceRef.current = null;
    replayedDevicesRef.current = new Set();
    setReplayProgress(null);
    setErrorMsg(null);
    setBootElapsed(0);
    setPhase(PHASE.IDLE);
    setStatusMsg("");
    if (termRef.current) {
      termRef.current.write("\r\n\x1b[33m[session ended — back to simulator]\x1b[0m\r\n");
    }
    toast.success("Real IOS session ended. Back to simulator.");
    if (onExit) onExit();
  }, [v1SessionId, v2SessionId, closeWs, onExit]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pollAbortRef.current = true;
      closeWs();
    };
  }, [closeWs]);

  // ----- UI -----------------------------------------------------------------
  const inRealMode = (
    phase === PHASE.WAITING_PROMPT
    || phase === PHASE.REPLAYING
    || phase === PHASE.READY
  );
  const inputDisabled = phase !== PHASE.READY;
  // Cold-boot monitor: shown from v1 provision until the console is live.
  // Hidden during device switches (same lab, nothing is booting).
  const estBootSec = EST_BOOT_SEC + Math.max(0, devices.length - 1) * 8;
  const showBootSeq = !switching && (
    phase === PHASE.BOOTING || phase === PHASE.CONNECTING || phase === PHASE.WAITING_PROMPT
  );

  // Header status — single boot message across BOOTING / CONNECTING /
  // WAITING_PROMPT phases (mimics the "booting a real router" feel).
  const headerStatus = (
    phase === PHASE.IDLE      ? null :
    switching                 ? `connecting to ${deviceId}…` :
    phase === PHASE.BOOTING   ? `booting ${devices.length > 1 ? `${devices.length} Cisco IOS nodes` : "Cisco IOS node"} · ${bootElapsed}s` :
    phase === PHASE.CONNECTING? "booting Cisco IOS…" :
    phase === PHASE.WAITING_PROMPT ? "booting Cisco IOS…" :
    phase === PHASE.REPLAYING ? `replay ${replayProgress?.idx || 0}/${replayProgress?.total || "?"}` :
    phase === PHASE.READY     ? "live" :
    phase === PHASE.ENDING    ? "ending…" :
    phase === PHASE.ERROR     ? "error" :
                                "booting Cisco IOS…"
  );

  return (
    <div
      className="flex flex-col h-full bg-black border-t border-zinc-800 font-term text-sm"
      data-testid="boot-real-ios-panel"
    >
      {/* Header — mirrors Terminal.jsx shell */}
      <div className="flex items-center justify-between px-4 py-1.5 border-b border-zinc-900 text-[10px] text-zinc-500 uppercase tracking-[0.25em]">
        <span className="flex items-center gap-2">
          <span className="flex gap-1">
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
          </span>
          <span className="ml-1">
            <span className="text-[#00FF41] mr-2">[{deviceId}]</span>
            Real Cisco IOS — {deviceId} — 9600 8-N-1
          </span>
        </span>
        <span className="flex items-center gap-3">
          {headerStatus && (
            <span className={
              phase === PHASE.ERROR ? "text-[#EF4444]" :
              phase === PHASE.READY ? "text-[#00FF41]" :
              "text-[#F59E0B]"
            }>
              {headerStatus}
            </span>
          )}
          {(phase !== PHASE.IDLE && phase !== PHASE.ENDING) && (
            <button
              onClick={handleEnd}
              data-testid="end-real-ios-btn"
              className="text-[10px] text-zinc-500 hover:text-[#EF4444] uppercase tracking-[0.25em]"
              title="End real-IOS session and return to simulator"
            >
              end session
            </button>
          )}
        </span>
      </div>

      {/* Body — xterm canvas (always mounted) with optional IDLE/BOOTING overlay.
          Padding matches Terminal.jsx's body (p-4) and reserves ~3rem at the
          bottom so the visual footprint is identical to the sim (which has an
          input-form row there). */}
      <div className="flex-1 relative min-h-0 bg-black">
        <div
          ref={termContainerRef}
          className="absolute inset-0 p-4 pb-12"
          data-testid="cml-console-xterm"
          style={{ opacity: phase === PHASE.IDLE ? 0.15 : (inputDisabled && inRealMode ? 0.92 : 1) }}
        />

        {phase === PHASE.IDLE && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="boot-idle-state">
            {notice && (
              <div className="max-w-xl px-4 py-2 border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-xs normal-case tracking-normal" data-testid="session-ended-notice">
                {notice}
              </div>
            )}
            <pre className="text-zinc-600 text-[10px] uppercase tracking-[0.25em] whitespace-pre-wrap">
              {`Cisco IOS XE Software (IOL 17.18)
        running on Cisco Modeling Labs (CML 2.10)
      • Real protocol convergence • Real packet forwarding •`}
            </pre>
            <button
              onClick={handleBoot}
              disabled={!isPro}
              data-testid="boot-real-ios-btn"
              className={`px-5 py-2 border text-xs font-term uppercase tracking-[0.25em] transition flex items-center gap-2 ${
                isPro
                  ? "border-[#00FF41]/60 text-[#00FF41] hover:bg-[#00FF41]/10"
                  : "border-zinc-800 text-zinc-600 cursor-not-allowed"
              }`}
              title={isPro ? "Boot a real Cisco IOS node (seconds from the warm pool)" : "Pro subscription required"}
            >
              {isPro ? "▸ boot real ios" : "▸ boot real ios (pro)"}
            </button>
            <div className="text-[10px] text-zinc-600 uppercase tracking-[0.25em]">
              warm slot: instant · cold boot ≈ {estBootSec}s{devices.length > 1 ? ` · ${devices.length} nodes` : ""}
            </div>
          </div>
        )}

        {showBootSeq && (
          <BootSequence
            devices={devices}
            elapsed={bootElapsed}
            estSec={estBootSec}
            done={phase !== PHASE.BOOTING}
          />
        )}

        {phase === PHASE.READY && idleLeft !== null && (
          <div className="absolute bottom-0 left-0 right-0 px-4 py-2 border-t border-[#F59E0B]/40 bg-[#F59E0B]/10 flex items-center justify-between" data-testid="idle-warning-band">
            <span className="text-[10px] text-[#F59E0B] uppercase tracking-[0.25em]">
              idle — session ends in {Math.floor(idleLeft / 60)}:{String(idleLeft % 60).padStart(2, "0")}
            </span>
            <span className="text-[10px] text-zinc-400 uppercase tracking-[0.2em]">press any key to stay connected</span>
          </div>
        )}

        {phase === PHASE.REPLAYING && replayProgress && (
          <div className="absolute bottom-0 left-0 right-0 px-4 py-2 border-t border-zinc-900 bg-black/80" data-testid="replay-status-band">
            <div className="text-[10px] text-[#F59E0B] uppercase tracking-[0.25em]">
              replaying {replayProgress.idx}/{replayProgress.total}
              {replayProgress.current ? <span className="text-zinc-500 ml-2 normal-case tracking-normal">› {replayProgress.current}</span> : null}
            </div>
          </div>
        )}

        {phase === PHASE.ERROR && (
          <div className="absolute bottom-0 left-0 right-0 px-4 py-3 border-t border-[#EF4444]/40 bg-[#EF4444]/10" data-testid="boot-error-band">
            <div className="text-xs text-[#EF4444] normal-case tracking-normal">{errorMsg}</div>
            <button
              onClick={handleRetry}
              data-testid="retry-real-ios-btn"
              className="mt-2 px-4 py-1.5 border border-[#00FF41]/60 text-xs text-[#00FF41] hover:bg-[#00FF41]/10 normal-case tracking-normal"
            >
              Retry Connection
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
